import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import {
  activeProcessSF,
  BACKUP_CREATE_SUMMARY_PATTERN,
  getActiveProcessKey,
  handleProcess,
  IRydeProcess,
  isProcessCanceledByUser,
  Log,
  RydeProcessTitle,
  RydeUiUnit,
  useProcesses,
} from "@ryde/shared";

import { useDynamicFormsFetch } from "../use-dynamic-forms-fetch";

export const useWatchBackupCreate = ({ entity }: { entity: RydeUiUnit }) => {
  const { t } = useTranslation();
  const { stopProcessPolling } = useProcesses();
  const { fetchTableRows } = useDynamicFormsFetch(entity);
  const backupCreateProcessKey = getActiveProcessKey(
    RydeProcessTitle.BackupCreate,
    entity,
  );
  const backupCreateProcess = useRecoilValue(
    activeProcessSF(backupCreateProcessKey),
  );

  const onComplete = useCallback(
    (process: IRydeProcess) => {
      if (isProcessCanceledByUser(process)) {
        return;
      }

      const summary = process.logs?.find(line => line.includes("entity dumps"));
      const counts = summary?.match(BACKUP_CREATE_SUMMARY_PATTERN);
      Log.successAndToast(
        t(
          "backup.create.success",
          "Backup complete: {{dumped}} of {{total}} entity dumps across {{scopes}} scope(s)",
          {
            dumped: counts ? Number(counts[1]) : 0,
            total: counts ? Number(counts[2]) : 0,
            scopes: counts ? Number(counts[3]) : 0,
          },
        ),
      );
      // The run just wrote new backup records; without this the grid keeps showing the pre-run
      // list until the page is reloaded by hand.
      fetchTableRows();
    },
    [t, fetchTableRows],
  );

  const onFail = useCallback(() => {
    // No toast here on purpose: the shared handler already surfaces the backend's reason (and the
    // abort notice when canceled), so adding one showed the same sentence twice. Scopes that
    // finished before the failure still wrote records, so the grid is refreshed either way.
    fetchTableRows();
  }, [fetchTableRows]);

  useEffect(() => {
    handleProcess({
      onComplete,
      onFail,
      entity,
      processTitle: RydeProcessTitle.BackupCreate,
      process: backupCreateProcess,
      stopProcessPolling,
      translationFunction: t,
    });
  }, [backupCreateProcess, entity, onComplete, onFail, stopProcessPolling, t]);
};
